import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {FormHelperText} from "@mui/material";
import {CustomStyleFormHelperText} from "../../../../../components/Styles";
import {CustomTextField} from "../../../../../components/CustomTextField";
import CustomPhotoPicker from "../../../../../components/PhotoPicker/CustomPhotoPicker";
import {getPhotoPicker, PHOTO_PICKER_MODEL} from "../../../../../components/PhotoPicker/SubComponent/Models/Models";
import CustomFileCard from "../../../../../components/CustomFileCard";
import ButtonAction from "../../../../../components/buttons/ButtonAction";
import ButtonIcon from "../../../../../components/buttons/ButtonIcon";
import {AddIcon, CleanIcon, DeleteIconsOptions} from "../../../../../components/buttons/IconButtons";
import {getIconByExtOrFile} from "../../../../../Apis/functions";
import NotInformation from "../../../../../components/NotInformation";
import {FILE_TYPE} from "../../../../../Helpers/fileType";
import {ACTION_MODEL} from "../../../../../Helpers/ActionModel";
import {guardDocumentPath} from "../../Model/Model";

/**
 * Documents of a guard, such as the DC-3, the criminal record or the medical certificate. A file is
 * given a name to ease its organization before it is added to the collection.
 */
const GuardDocumentsStep = ({documents, addDocument, removeDocument, downloadDocument, disabled = false, emptyFields = false, onPendingChange}) => {
    const {t} = useTranslation();
    const [name, setName] = useState('');
    const [photoPicker, setPhotoPicker] = useState({...PHOTO_PICKER_MODEL});

    const handleClear = () => {
        setName('');
        setPhotoPicker({...PHOTO_PICKER_MODEL});
    };

    const file = getPhotoPicker(photoPicker);
    // The name and the file become required once either of them is captured
    const isFilling = name.trim() !== '' || !!file;
    const isComplete = name.trim() !== '' && !!file;

    useEffect(() => {
        if (onPendingChange) onPendingChange(isFilling);
    }, [isFilling]);

    const handleAdd = () => {
        addDocument(name.trim(), file);
        handleClear();
    };

    return (
        <div className={'row mt-3'}>
            {!disabled &&
                <>
                    <div className='col-12 d-flex justify-content-end mb-2'>
                        <ButtonIcon
                            className='btn-clean-secondary-600'
                            id='btn_gds_clean'
                            Icon={CleanIcon}
                            title={'clear_instances'}
                            iconProps={'btn-icon-svg-12px icon-color-general-00'}
                            onClick={handleClear}
                        />
                    </div>
                    <div className={'col-12'}>
                        <CustomTextField
                            id={"txt_gds_document_name"}
                            name={"name"}
                            value={name}
                            trans={"document_name"}
                            type={"text"}
                            length={128}
                            onChange={(e) => setName(e.target.value)}
                            emptyFields={emptyFields}
                            required={isFilling}
                        />
                    </div>
                    <div className={'col-12'}>
                        <CustomPhotoPicker
                            id={"fu_gds_document"}
                            photoPicker={photoPicker}
                            setPhotoPicker={setPhotoPicker}
                            actionModel={ACTION_MODEL.CREATE}
                            accept={{...FILE_TYPE.FILES}}
                            classWith={'w-100'}
                            showPreview={false}
                            required={isFilling}
                            emptyFields={emptyFields && !file}
                        />
                    </div>
                    <div className='col-12 mt-3 d-flex justify-content-end'>
                        <ButtonAction
                            id='btn_gds_add_new_documentation'
                            name='add_new_documentation'
                            Icon={AddIcon}
                            splitName={false}
                            className='btn-secondary-600'
                            disabled={!isComplete}
                            onClick={handleAdd}
                        />
                    </div>
                </>
            }

            <div className={'col-12 mt-3'}>
                <div className={`p-3 border-radius-8px ${emptyFields ? 'border-full-error-code-600' : 'border-full-shades-300'}`}>
                    {documents.length > 0 ?
                        <div className={'row'}>
                            {documents.map((document) => (
                                <div key={document.id} className={'col-12 mb-3'}>
                                    <CustomFileCard
                                        Icon={getIconByExtOrFile(document.file || document.fileName)}
                                        buttonId={`btn_gds_document_${document.id}`}
                                        title={document.name}
                                        subtitle={document.fileName}
                                        fileValue={document.fileName}
                                        // Only a saved document has a file to download
                                        showBtnDownload={!!guardDocumentPath(document) && !!downloadDocument}
                                        onClick={() => downloadDocument(document)}
                                        classContent='px-0 py-0'
                                        deleteIcon={DeleteIconsOptions}
                                        showBtnDelete={!disabled}
                                        clickBtnDelete={() => removeDocument(document)}
                                    />
                                </div>
                            ))}
                        </div>
                        :
                        <NotInformation card={true} text={t('no_documents_uploaded')}/>
                    }
                </div>
            </div>
            <div className={'col-12 mt-1'}>
                <FormHelperText error={true} sx={CustomStyleFormHelperText(false)}>
                    {emptyFields && t('add_documentation_or_clear_fields')}
                </FormHelperText>
            </div>
        </div>
    );
};

export default GuardDocumentsStep;
